import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Button,
  TextField,
  Grid,
  Typography,
  MenuItem,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getAllProducts, getAreas } from "../../api/Api";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90vw", sm: "80vw", md: "80vw", lg: "70vw" },
  maxHeight: "80vh",
  bgcolor: "background.paper",
  borderRadius: 1,
  boxShadow: 24,
  overflowY: "auto",
};

const CustomerOrderModal = ({ open, handleClose, handleSubmit }) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [areas, setAreas] = useState([]);
  const initialOrderData = {
    fullName: "",
    contactNo: "",
    item: "",
    price: "",
    quantity: "",
    area: "",
    deliveryAddress: "",
    deliveryDate: "",
  };
  const [orderData, setOrderData] = useState(initialOrderData);

  const isFormValid = Object.values(orderData).every((value) => value !== "");

  useEffect(() => {
    const allProducts = async () => {
      try {
        const response = await getAllProducts();
        const productData = response.data.products.map((product) => ({
          name: product.name,
          price: product.price,
        }));
        setProducts(productData);
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };
    allProducts();
  }, []);
  useEffect(() => {
    const fetchAreas = async () => {
      try {
        const response = await getAreas();
        const areasData = response.data.regions.map(
          (region) => region.region_name
        );
        // Adding "Bhubaneswar" to the areas list
        setAreas([...areasData, "Bhubaneswar"]);
      } catch (error) {
        console.error("Error fetching areas:", error);
      }
    };
    fetchAreas();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "quantity" && value < 0) return;

    if (name === "item") {
      const selectedProduct = products.find(
        (product) => product.name === value
      );
      setOrderData({
        ...orderData,
        item: value,
        price: selectedProduct ? selectedProduct.price : "",
      });
    } else {
      setOrderData({ ...orderData, [name]: value });
    }
  };

  const resetForm = () => {
    setOrderData(initialOrderData);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    handleSubmit(orderData);
    resetForm(); // Reset form after submission
    handleClose();
    navigate("/PaymentSection", { state: { orderData } });
  };

  const handleCancel = () => {
    resetForm(); // Reset form on cancel
    handleClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style} component="form" onSubmit={onSubmit}>
        <Grid container spacing={2} sx={{ p: { xs: 2, sm: 3 } }}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Full Name"
              name="fullName"
              value={orderData.fullName}
              onChange={handleChange}
              required
              sx={{
                backgroundColor: "#D9D9D9",
                height: { xs: "50px", sm: "60px" },
              }}
              InputProps={{ sx: { height: { xs: "50px", sm: "60px" } } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Contact No"
              name="contactNo"
              value={orderData.contactNo}
              onChange={handleChange}
              required
              sx={{
                backgroundColor: "#D9D9D9",
                height: { xs: "50px", sm: "60px" },
              }}
              InputProps={{ sx: { height: { xs: "50px", sm: "60px" } } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              select
              label="Select Item"
              name="item"
              value={orderData.item}
              onChange={handleChange}
              required
              sx={{
                backgroundColor: "#D9D9D9",
                height: { xs: "50px", sm: "60px" },
              }}
              InputProps={{ sx: { height: { xs: "50px", sm: "60px" } } }}
            >
              {products.map((product) => (
                <MenuItem key={product.name} value={product.name}>
                  {product.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Quantity"
              name="quantity"
              type="number"
              value={orderData.quantity}
              onChange={handleChange}
              required
              sx={{
                backgroundColor: "#D9D9D9",
                height: { xs: "50px", sm: "60px" },
              }}
              InputProps={{
                sx: { height: { xs: "50px", sm: "60px" } },
                inputProps: { min: 0 },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <TextField
              fullWidth
              label="Delivery Address"
              name="deliveryAddress"
              value={orderData.deliveryAddress}
              onChange={handleChange}
              multiline
              rows={3}
              required
              sx={{ backgroundColor: "#D9D9D9" }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              select
              label="Area"
              name="area"
              value={orderData.area}
              onChange={handleChange}
              required
              sx={{
                backgroundColor: "#D9D9D9",
                height: { xs: "50px", sm: "60px" },
              }}
              InputProps={{ sx: { height: { xs: "50px", sm: "60px" } } }}
            >
              {areas.map((area, index) => (
                <MenuItem key={index} value={area}>
                  {area}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Delivery Date"
              name="deliveryDate"
              InputLabelProps={{ shrink: true }}
              value={orderData.deliveryDate}
              onChange={handleChange}
              required
              sx={{
                backgroundColor: "#D9D9D9",
                height: { xs: "50px", sm: "60px" },
              }}
              InputProps={{ sx: { height: { xs: "50px", sm: "60px" } } }}
            />
          </Grid>
        </Grid>

        <Grid
          container
          spacing={2}
          sx={{ p: { xs: 2, sm: 3 }, mt: 1 }}
          alignItems="center"
          justifyContent="space-between"
        >
          <Grid item xs={12} sm={7}>
            <Typography
              variant="caption"
              sx={{ color: "#FF5558", fontSize: { xs: "11px", sm: "13px" } }}
            >
              Please order here for bulk order (Min 10pcs) | For monthly order,
              use our{" "}
              <span
                style={{
                  color: "red",
                  fontWeight: "600",
                  textDecoration: "none",
                }}
              >
                Drips Water
              </span>{" "}
              App.
            </Typography>
          </Grid>
          <Grid
            container
            item
            xs={12}
            sm={5}
            spacing={1}
            justifyContent="flex-end"
          >
            <Grid item xs={6} sm={5}>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  backgroundColor: "#36AB5D",
                  p: { xs: "8px", sm: "10px" },
                }}
                disabled={!isFormValid}
                fullWidth
              >
                Place Order
              </Button>
            </Grid>
            <Grid item xs={6} sm={5}>
              <Button
                onClick={handleCancel}
                variant="outlined"
                sx={{
                  backgroundColor: "#EA4261",
                  color: "#fff",
                  p: { xs: "8px", sm: "10px" },
                }}
                fullWidth
              >
                Cancel
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

export default CustomerOrderModal;
